import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthLayout, Field, SubmitBtn, ErrorMsg } from "@/pages/Login";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [invite, setInvite] = useState(null); // null=loading, false=invalid, object=ok
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return setInvite(false);
    api.get(`/auth/invite/${token}`)
      .then(({ data }) => setInvite(data))
      .catch(() => setInvite(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters");
    if (password !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, password });
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (invite === null)
    return <AuthLayout title="Loading invite…"><div className="h-6 w-6 border-2 border-slate-300 border-t-blue-700 rounded-full animate-spin" /></AuthLayout>;

  if (invite === false)
    return (
      <AuthLayout title="Invite invalid">
        <p data-testid="invite-invalid" className="text-sm text-slate-600">
          This invite link is invalid, expired, or already used. Please ask your administrator for a new one.
        </p>
      </AuthLayout>
    );

  return (
    <AuthLayout title="Accept your invite" description={`Set a password for ${invite.email}`}>
      <form onSubmit={submit} data-testid="accept-invite-form">
        <ErrorMsg>{error}</ErrorMsg>
        <Field label="New password" type="password" required value={password} data-testid="invite-password"
          onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        <Field label="Confirm password" type="password" required value={confirm} data-testid="invite-confirm"
          onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" />
        <SubmitBtn loading={loading} data-testid="invite-submit">Activate account</SubmitBtn>
      </form>
    </AuthLayout>
  );
}
